// src/components/hooks/hooks-e-commerce/useAddressUser.js
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export const useAddressUser = () => {
    const [addresses, setAddresses] = useState([]);
    const [defaultAddress, setDefaultAddress] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // ✅ 1. RECUPERA TUTTI GLI INDIRIZZI DELL'UTENTE
    const fetchAddresses = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await axios.get('/api/users/addresses', {
                withCredentials: true
            });
            
            if (response.data.success) {
                const addressList = response.data.addresses || [];
                setAddresses(addressList);
                
                // Trova l'indirizzo predefinito
                const defaultAddr = addressList.find(a => a.predefinito === true);
                setDefaultAddress(defaultAddr || null);
                
                console.log('📦 Indirizzi recuperati:', addressList.length);
                console.log('📍 Indirizzo predefinito:', defaultAddr);
            }
        } catch (err) {
            console.error('❌ Errore recupero indirizzi:', err);
            setError(err.response?.data?.message || 'Errore nel recupero degli indirizzi');
        } finally {
            setLoading(false);
        }
    }, []);

    // ✅ 2. IMPOSTA UN INDIRIZZO COME PREDEFINITO
    const setAsDefault = useCallback(async (addressId) => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await axios.patch(
                `/api/users/addresses/${addressId}/set-default`,
                {},
                { withCredentials: true }
            );
            
            if (response.data.success) {
                // Aggiorna la lista localmente
                const updatedAddresses = addresses.map(a => ({
                    ...a,
                    predefinito: a.id === addressId
                }));
                setAddresses(updatedAddresses);
                
                const newDefault = updatedAddresses.find(a => a.predefinito === true);
                setDefaultAddress(newDefault || null);
                
                console.log(`✅ Indirizzo ${addressId} impostato come predefinito`);
                return true;
            }
            return false;
        } catch (err) {
            console.error('❌ Errore impostazione predefinito:', err);
            setError(err.response?.data?.message || 'Errore nell\'impostazione dell\'indirizzo predefinito');
            return false;
        } finally {
            setLoading(false);
        }
    }, [addresses]);

    // ✅ 3. ELIMINA UN INDIRIZZO (soft delete se collegato a ordini)
    const deleteAddress = useCallback(async (addressId) => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await axios.delete(
                `/api/users/addresses/${addressId}`,
                { withCredentials: true }
            );
            
            if (response.data.success) {
                // Rimuovi l'indirizzo dalla lista locale
                const updatedAddresses = addresses.filter(a => a.id !== addressId);
                setAddresses(updatedAddresses);
                
                // Se l'indirizzo eliminato era il predefinito, trova il nuovo default
                if (defaultAddress?.id === addressId) {
                    const newDefault = updatedAddresses.find(a => a.predefinito === true) || null;
                    setDefaultAddress(newDefault);
                }
                
                console.log(`✅ Indirizzo ${addressId} eliminato/disattivato`);
                return true;
            }
            return false;
        } catch (err) {
            console.error('❌ Errore eliminazione indirizzo:', err);
            setError(err.response?.data?.message || 'Errore nell\'eliminazione dell\'indirizzo');
            return false;
        } finally {
            setLoading(false);
        }
    }, [addresses, defaultAddress]);

    // ✅ 4. AGGIUNGE UN NUOVO INDIRIZZO
    const addAddress = useCallback(async (addressData) => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await axios.post(
                '/api/users/addresses',
                addressData,
                { withCredentials: true }
            );
            
            if (response.data.success) {
                // Aggiungi il nuovo indirizzo alla lista
                const newAddress = response.data.address;
                const updatedAddresses = [...addresses, newAddress];
                setAddresses(updatedAddresses);
                
                // Se è predefinito, aggiorna il default
                if (newAddress.predefinito) {
                    setDefaultAddress(newAddress);
                }
                
                console.log(`✅ Nuovo indirizzo creato: ${newAddress.id}`);
                return newAddress;
            }
            return null;
        } catch (err) {
            console.error('❌ Errore creazione indirizzo:', err);
            setError(err.response?.data?.message || 'Errore nella creazione dell\'indirizzo');
            return null;
        } finally {
            setLoading(false);
        }
    }, [addresses]);

    // ✅ 5. AGGIORNA UN INDIRIZZO ESISTENTE
    const updateAddress = useCallback(async (addressId, updateData) => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await axios.put(
                `/api/users/addresses/${addressId}`,
                updateData,
                { withCredentials: true }
            );
            
            if (response.data.success) {
                const updatedAddress = response.data.address;
                
                // Aggiorna la lista locale
                const updatedAddresses = addresses.map(a => 
                    a.id === addressId ? updatedAddress : a
                );
                setAddresses(updatedAddresses);
                
                // Se è predefinito, aggiorna il default
                if (updatedAddress.predefinito) {
                    setDefaultAddress(updatedAddress);
                }
                
                console.log(`✅ Indirizzo ${addressId} aggiornato`);
                return updatedAddress;
            }
            return null;
        } catch (err) {
            console.error('❌ Errore aggiornamento indirizzo:', err);
            setError(err.response?.data?.message || 'Errore nell\'aggiornamento dell\'indirizzo');
            return null;
        } finally {
            setLoading(false);
        }
    }, [addresses]);

    // ✅ 6. OTTIENE GLI INDIRIZZI DI SPEDIZIONE
    const getShippingAddresses = useCallback(() => {
        return addresses.filter(a => a.tipo_indirizzo === 'spedizione');
    }, [addresses]);

    // ✅ 7. OTTIENE GLI INDIRIZZI DI FATTURAZIONE
    const getBillingAddresses = useCallback(() => {
        return addresses.filter(a => a.tipo_indirizzo === 'fatturazione');
    }, [addresses]);

    // Carica gli indirizzi all'avvio
    useEffect(() => {
        fetchAddresses();
    }, [fetchAddresses]);

    return {
        // Stato
        addresses,
        defaultAddress,
        loading,
        error,
        
        // Metodi
        fetchAddresses,
        setAsDefault,
        deleteAddress,
        addAddress,
        updateAddress,
        getShippingAddresses,
        getBillingAddresses,
        
        // Utility
        hasAddresses: addresses.length > 0,
        hasDefaultAddress: !!defaultAddress,
    };
};

export default useAddressUser;